import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTonAddress, useTonConnectUI } from "@tonconnect/ui-react";
import { useParams } from "react-router-dom";
import { Address, beginCell, OpenedContract, toNano } from "ton-core";
import { NoneActionProps } from "../components/fields/NoneActionFields";
import { SendJettonActionProps } from "../components/fields/SendJettonFields";
import { SendNftActionProps } from "../components/fields/SendNftFields";
import { SendTonActionProps } from "../components/fields/SendTonFields";
import { UpdateContentActionProps } from "../components/fields/UpdateContentFields";
import { UpdateQuorumActionProps } from "../components/fields/UpdateQuorumFields";
import { MintTokensActionProps } from "../components/NewProposal";
import { Dao } from "../wrappers/Dao";
import {
  buildOnchainMetadata,
  readOnchainMetadata,
} from "../wrappers/onchainContent";
import { proposalConfigToCell } from "../wrappers/Proposal";
import { formatDecimals } from "./deployerService";
import { getTonClient } from "./tonClient";
import { getSeqno, waitWalletUpdate } from "./walletService";

export interface DaoContent {
  image: string;
  name: string;
  description: string;
}

export const useDaoJettonData = () => {
  const { address } = useParams();
  return useQuery(
    [address, "jetton"],
    async () => {
      const tonClient = await getTonClient();
      const dao = tonClient.open(
        Dao.createFromAddress(Address.parse(address!))
      );

      const data = await dao.getJettonData();
      return readOnchainMetadata<DaoContent>(data.content, [
        "image",
        "name",
        "description",
      ]);
    },
    { enabled: address != null }
  );
};

export const useDaoCollectionData = () => {
  const { address } = useParams();
  return useQuery(
    [address, "collection"],
    async () => {
      const tonClient = await getTonClient();
      const dao = tonClient.open(
        Dao.createFromAddress(Address.parse(address!))
      );

      const { nextProposalId } = await dao.getCollectionData();
      return nextProposalId;
    },
    { enabled: address != null, keepPreviousData: true }
  );
};

export const useDaoData = () => {
  const { address } = useParams();
  return useQuery(
    [address, "dao"],
    async () => {
      const tonClient = await getTonClient();
      const dao = tonClient.open(
        Dao.createFromAddress(Address.parse(address!))
      );

      return await dao.getDaoData();
    },
    { enabled: address != null }
  );
};

export type AuctionParams =
  | NoneActionProps
  | MintTokensActionProps
  | SendTonActionProps
  | SendJettonActionProps
  | SendNftActionProps
  | UpdateContentActionProps
  | UpdateQuorumActionProps;

export interface CreateProposalProps {
  image: string;
  expirationDate: Date;
  action: AuctionParams;
}

export enum ProposalActions {
  none = "none",
  mintTokens = "mintTokens",
  sendTon = "sendTon",
  sendJetton = "sendJetton",
  sendNft = "sendNft",
  changeQuorum = "changeQuorum",
  changeContent = "changeContent",
}

const actionToCell = async (
  dao: OpenedContract<Dao>,
  action: AuctionParams
) => {
  switch (action.kind) {
    case ProposalActions.mintTokens: {
      const data = await dao.getDaoData();

      return [
        toNano("0.05"),
        beginCell()
          .storeBuilder(Dao.selfMessage(dao.address))
          .storeBuilder(
            Dao.mintTokensMessage(
              Address.parse(action.address),
              BigInt(
                formatDecimals(
                  parseFloat(action.jettonAmount),
                  parseInt(data.daoContent.decimals)
                )
              )
            )
          )
          .endCell(),
      ] as const;
    }

    case ProposalActions.sendTon: {
      return [
        toNano("0.05"),
        Dao.sendTonMessage(
          Address.parse(action.address),
          toNano(action.tonAmount),
          action.comment
        ).endCell(),
      ] as const;
    }

    case ProposalActions.sendJetton: {
      return [
        toNano("0.1"),
        beginCell()
          .storeBuilder(
            Dao.sendMessageRef(
              action.jettonData?.jettonWalletAddress!,
              toNano("0.1")
            )
          )
          .storeRef(
            Dao.sendJettonMessage({
              toAddress: Address.parse(action.address),
              jettonAmount: BigInt(
                formatDecimals(
                  action.jettonAmount,
                  parseInt(action.jettonData?.jettonContent?.decimals ?? "9")
                )
              ),
              responseAddress: dao.address,
            })
          )
          .endCell(),
      ] as const;
    }

    case ProposalActions.sendNft: {
      return [
        toNano("0.1"),
        beginCell()
          .storeBuilder(
            Dao.sendMessageRef(Address.parse(action.nftAddress), toNano("0.1"))
          )
          .storeRef(
            Dao.sendNftMessage({
              newOwnerAddress: Address.parse(action.address),
              responseAddress: dao.address,
            })
          )
          .endCell(),
      ] as const;
    }

    case ProposalActions.changeContent: {
      const data = await dao.getDaoData();

      return [
        toNano("0.05"),
        beginCell()
          .storeBuilder(Dao.selfMessage(dao.address))
          .storeBuilder(
            Dao.updateContentMessage(
              buildOnchainMetadata({
                image: action.daoImage,
                name: action.daoName,
                description: action.daoDescription,
                decimals: data.daoContent.decimals,
                symbol: data.daoContent.symbol,
              })
            )
          )
          .endCell(),
      ] as const;
    }

    case ProposalActions.changeQuorum: {
      const [quorumNominator, quorumDenominator] = action.quorum.split("/");

      return [
        toNano("0.05"),
        beginCell()
          .storeBuilder(Dao.selfMessage(dao.address))
          .storeBuilder(
            Dao.updateQuorumMessage(
              parseInt(quorumNominator),
              parseInt(quorumDenominator)
            )
          )
          .endCell(),
      ] as const;
    }

    default:
      return [toNano("0.002"), beginCell().endCell()] as const;
  }
};

const buildProposalContent = async (
  dao: OpenedContract<Dao>,
  { image, action }: CreateProposalProps
) => {
  if ("isCustomMessage" in action && action.isCustomMessage) {
    return buildOnchainMetadata({
      name: action.name!,
      image: image,
      description: action.description!,
    });
  }
  switch (action.kind) {
    case ProposalActions.none:
      return buildOnchainMetadata({
        name: action.name,
        image: image,
        description: action.description,
      });
    case ProposalActions.mintTokens: {
      const data = await dao.getDaoData();
      return buildOnchainMetadata({
        name: "Mint DAO Jetton",
        image: image,
        description: `Mint ${action.jettonAmount} ${data.daoContent.symbol} for ${action.address}.`,
      });
    }

    case ProposalActions.sendTon:
      return buildOnchainMetadata({
        name: "Send TON",
        image: image,
        description: `Send ${action.tonAmount} TON to ${
          action.address
        } from DAO treasure.${
          action.comment ? ` Comment: "${action.comment}".` : ""
        }`,
      });

    case ProposalActions.sendJetton:
      return buildOnchainMetadata({
        name: "Send Jetton",
        image: image,
        description: `Send ${action.jettonAmount} ${action.jettonData?.jettonContent?.symbol} to ${action.address} from DAO treasure.`,
      });

    case ProposalActions.sendNft:
      return buildOnchainMetadata({
        name: "Send NFT",
        image: image,
        description: `Send NFT ${action.nftAddress} to ${action.address} from DAO treasure.`,
      });

    case ProposalActions.changeContent:
      return buildOnchainMetadata({
        name: "Update DAO Content",
        image: image,
        description: `Update DAO Content. Name: "${action.daoName}"; Image: "${action.daoImage}"; Description: "${action.daoDescription}".`,
      });

    case ProposalActions.changeQuorum:
      return buildOnchainMetadata({
        name: "Update DAO Quorum",
        image: image,
        description: `Update DAO Quorum to "${action.quorum}" of total jetton supply.`,
      });

    default:
      return buildOnchainMetadata({
        name: "Unknown",
        image: image,
        description: "Unknown",
      });
  }
};

export const useCreateProposal = () => {
  const { address } = useParams();
  const owner = useTonAddress(false);
  const [tonConnectUi] = useTonConnectUI();

  const client = useQueryClient();
  return useMutation<void, Error, CreateProposalProps>(async (params) => {
    const tonClient = await getTonClient();

    const daoAddress = Address.parse(address!);
    const dao = tonClient.open(Dao.createFromAddress(daoAddress));

    const [minimalExecutionAmount, messageToSend] = await actionToCell(
      dao,
      params.action
    );

    const content = await buildProposalContent(dao, params);

    const walletSeqno = await getSeqno(owner);

    await tonConnectUi.sendTransaction({
      validUntil: Date.now() + 1000000,
      messages: [
        {
          address: daoAddress.toRawString(),
          amount: "200000000", // 0.2 TON
          payload: Dao.scheduleProposalMessage(
            params.expirationDate,
            proposalConfigToCell({
              minimalExecutionAmount,
              messagesToSend: [messageToSend],
              content,
            })
          )
            .toBoc()
            .toString("base64"),
        },
      ],
    });

    await waitWalletUpdate(owner, walletSeqno);

    await client.invalidateQueries([address, "collection"]);
  });
};
